import { supabase } from '@/integrations/supabase/client';

export interface ReportDetails {
	total_reports: number;
	latest_report_date: string | null;
}

/**
 * Check if a developer has already reported a creator
 * @param developerId - ID of the developer
 * @param creatorId - ID of the creator
 * @returns Promise<boolean>
 */
export async function hasDeveloperReportedCreator(developerId: string, creatorId: string): Promise<boolean> {
	try {
		const { data, error } = await supabase.rpc('has_developer_reported_creator', {
			developer_id_param: developerId,
			creator_id_param: creatorId
		});

		if (error) {
			console.error('Error checking report status:', error);
			return false;
		}

		return data || false;
	} catch (error) {
		console.error('Error checking report status:', error);
		return false;
	}
}

/**
 * Add a report for a creator
 * @param developerId - ID of the developer making the report
 * @param creatorId - ID of the creator being reported
 * @returns Promise<boolean> - true if report was added, false if already exists
 */
export async function addReport(developerId: string, creatorId: string): Promise<boolean> {
	try {
		const { data, error } = await supabase.rpc('add_report', {
			developer_id_param: developerId,
			creator_id_param: creatorId
		});

		if (error) {
			console.error('Error adding report:', error);
			throw error;
		}

		return data || false;
	} catch (error) {
		console.error('Error adding report:', error);
		throw error;
	}
}

/**
 * Remove a report for a creator
 * @param developerId - ID of the developer who made the report
 * @param creatorId - ID of the creator being unreported
 * @returns Promise<boolean> - true if report was removed, false if didn't exist
 */
export async function removeReport(developerId: string, creatorId: string): Promise<boolean> {
	try {
		const { data, error } = await supabase.rpc('remove_report', {
			developer_id_param: developerId,
			creator_id_param: creatorId
		});

		if (error) {
			console.error('Error removing report:', error);
			throw error;
		}

		return data || false;
	} catch (error) {
		console.error('Error removing report:', error);
		throw error;
	}
}

/**
 * Get report details for a creator
 * @param creatorId - ID of the creator
 * @returns Promise<ReportDetails | null>
 */
export async function getCreatorReportDetails(creatorId: string): Promise<ReportDetails | null> {
	try {
		const { data, error } = await supabase.rpc('get_creator_report_details', {
			creator_id_param: creatorId
		});

		if (error) {
			console.error('Error getting report details:', error);
			return null;
		}

		if (data && data.length > 0) {
			return {
				total_reports: data[0].total_reports || 0,
				latest_report_date: data[0].latest_report_date
			};
		}

		return { total_reports: 0, latest_report_date: null };
	} catch (error) {
		console.error('Error getting report details:', error);
		return null;
	}
}

/**
 * Format report date for display
 * @param dateString - ISO date string
 * @returns Formatted date string (DD/MM/YYYY)
 */
export function formatReportDate(dateString: string | null): string {
	if (!dateString) return '';
	
	try {
		const date = new Date(dateString);
		return date.toLocaleDateString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	} catch (error) {
		console.error('Error formatting date:', error);
		return '';
	}
}

/**
 * Get trust score for a creator
 * @param creatorId - Creator ID
 * @returns Trust score (0-200)
 */
export async function getCreatorTrustScore(creatorId: string): Promise<number> {
	try {
		// Récupérer directement le trust_score stocké dans la table profiles
		const { data, error } = await supabase
			.from('profiles')
			.select('trust_score')
			.eq('id', creatorId)
			.single();

		if (error) {
			console.error('Error getting trust score:', error);
			return 100; // Default score
		}

		// Retourner le score stocké, ou 100 par défaut si null
		return (data as any)?.trust_score ?? 100;
	} catch (error) {
		console.error('Error getting trust score:', error);
		return 100; // Default score
	}
}

/**
 * Gets the trust score emoji based on its value
 * @param score - Trust score (0-200)
 * @returns Emoji representing trust level
 */
export function getTrustScoreEmoji(score: number): string {
	if (score >= 150) return '🤩'; // Excellent (très positif)
	if (score > 100) return '😊'; // Bon (positif)
	if (score === 100) return '😐'; // Neutre
	if (score >= 60) return '😕'; // Moyen (négatif)
	return '😠'; // Risque élevé (très négatif)
}

/**
 * Gets the trust score color based on its value
 * @param score - Trust score (0-200)
 * @returns CSS class for the color
 */
export function getTrustScoreColor(score: number): string {
	if (score >= 150) return 'text-green-600'; // Excellent - Vert
	if (score > 100) return 'text-green-500'; // Bon - Vert clair
	if (score === 100) return 'text-gray-500'; // Neutre - Gris
	if (score >= 60) return 'text-yellow-600'; // Moyen - Jaune
	return 'text-red-600'; // Risque élevé - Rouge
}

/**
 * Gets the trust score label based on its value
 * @param score - Trust score (0-200)
 * @returns Trust level label
 */
export function getTrustScoreLabel(score: number): string {
	if (score >= 150) return 'Trusted';
	if (score > 100) return 'Reliable';
	if (score === 100) return 'Neutral';
	if (score >= 60) return 'Ok';
	return 'Unworthy';
}
